import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Shield,
  Search,
  Filter,
  Download,
  Calendar,
  Clock,
  User,
  FileText,
  Upload,
  Trash2,
  CheckCircle,
  AlertCircle,
  Play,
  RefreshCw,
  Eye,
  Lock,
  Unlock,
  UserPlus,
  UserMinus,
  ChevronDown,
  ChevronRight,
  Activity,
  LayoutDashboard,
  Settings,
  LogOut,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event type configuration
const EVENT_CONFIG = {
  upload_created: { icon: Upload, color: "emerald", label: "Upload Created" },
  upload_deleted: { icon: Trash2, color: "red", label: "Upload Deleted" },
  upload_restored: { icon: RefreshCw, color: "blue", label: "Upload Restored" },
  transcription_queued: { icon: Clock, color: "amber", label: "Transcription Queued" },
  transcription_started: { icon: Play, color: "blue", label: "Transcription Started" },
  transcription_completed: { icon: CheckCircle, color: "emerald", label: "Transcription Completed" },
  transcription_failed: { icon: AlertCircle, color: "red", label: "Transcription Failed" },
  transcription_retried: { icon: RefreshCw, color: "amber", label: "Transcription Retried" },
  attendance_added: { icon: UserPlus, color: "emerald", label: "Attendance Added" },
  attendance_removed: { icon: UserMinus, color: "red", label: "Attendance Removed" },
  minutes_generated: { icon: FileText, color: "purple", label: "Minutes Generated" },
  minutes_exported: { icon: Download, color: "blue", label: "Minutes Exported" },
  minutes_approved: { icon: CheckCircle, color: "emerald", label: "Minutes Approved" },
  minutes_signed: { icon: Lock, color: "purple", label: "Minutes Signed" },
  minutes_locked: { icon: Lock, color: "slate", label: "Minutes Locked" },
  meeting_deleted: { icon: Trash2, color: "red", label: "Meeting Deleted" },
  resolution_added: { icon: FileText, color: "blue", label: "Resolution Added" },
  declaration_added: { icon: Shield, color: "amber", label: "Declaration Added" },
};

const DEFAULT_EVENT_CONFIG = { icon: Activity, color: "slate", label: "Event" };

export default function AuditCenterPage() {
  const navigate = useNavigate();
  const { user, logout, companyBranding } = useAuth();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    eventType: "",
    meetingId: "",
    dateFrom: "",
    dateTo: "",
    searchQuery: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    byType: {},
  });

  const fetchAuditEvents = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/audit?limit=200`;
      if (filters.eventType) url += `&event_type=${filters.eventType}`;
      if (filters.meetingId) url += `&meeting_id=${filters.meetingId}`;
      
      const response = await axios.get(url);
      let eventList = response.data.events || [];
      
      // Client-side filtering for search and date range
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        eventList = eventList.filter(e => 
          e.event_type?.toLowerCase().includes(query) ||
          e.meeting_id?.toLowerCase().includes(query) ||
          JSON.stringify(e.metadata)?.toLowerCase().includes(query)
        );
      }
      
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        eventList = eventList.filter(e => new Date(e.created_at) >= fromDate);
      }
      
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59);
        eventList = eventList.filter(e => new Date(e.created_at) <= toDate);
      }
      
      setEvents(eventList);
      
      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayEvents = eventList.filter(e => new Date(e.created_at) >= today);
      const byType = eventList.reduce((acc, e) => {
        acc[e.event_type] = (acc[e.event_type] || 0) + 1;
        return acc;
      }, {});
      
      setStats({
        total: eventList.length,
        today: todayEvents.length,
        byType,
      });
    } catch (error) {
      toast.error("Failed to load audit events");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAuditEvents();
  }, [fetchAuditEvents]);

  const handleExportCSV = () => {
    if (events.length === 0) {
      toast.error("No events to export");
      return;
    }
    
    const headers = ["Timestamp", "Event Type", "Meeting ID", "User ID", "Details"];
    const rows = events.map(e => [
      e.created_at,
      e.event_type,
      e.meeting_id || "",
      e.user_id || "",
      JSON.stringify(e.metadata || {}).replace(/"/g, '""')
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Audit log exported");
  };

  const clearFilters = () => {
    setFilters({
      eventType: "",
      meetingId: "",
      dateFrom: "",
      dateTo: "",
      searchQuery: "",
    });
  };

  const getEventConfig = (eventType) => EVENT_CONFIG[eventType] || DEFAULT_EVENT_CONFIG;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
                {companyBranding?.logoUrl ? (
                  <img src={companyBranding.logoUrl} alt="Logo" className="h-8 w-auto" />
                ) : (
                  <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="font-heading font-semibold text-primary-700">
                  {companyBranding?.companyName || "MyMeetingsPal"}
                </span>
              </div>
              
              <nav className="hidden md:flex items-center gap-1">
                <Button variant="ghost" className="text-stone-600" onClick={() => navigate("/")}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button variant="ghost" className="text-primary-600 bg-primary-50">
                  <Shield className="w-4 h-4 mr-2" />
                  Audit Center
                </Button>
              </nav>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-stone-800">{user?.name}</p>
                <p className="text-xs text-stone-500 capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-500">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary-600" />
              Audit Center
            </h1>
            <p className="text-slate-500 mt-1">Complete audit trail of all system activities</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchAuditEvents} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Events</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.today}</p>
                <p className="text-xs text-slate-500">Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {(stats.byType.minutes_generated || 0) + (stats.byType.minutes_exported || 0)}
                </p>
                <p className="text-xs text-slate-500">Minutes Actions</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {(stats.byType.upload_created || 0) + (stats.byType.transcription_completed || 0)}
                </p>
                <p className="text-xs text-slate-500">Uploads & Transcriptions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search events..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  className="pl-9"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-slate-100" : ""}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {(filters.eventType || filters.meetingId || filters.dateFrom || filters.dateTo) && (
                  <span className="ml-2 w-2 h-2 bg-primary-500 rounded-full" />
                )}
              </Button>
              {(filters.eventType || filters.meetingId || filters.dateFrom || filters.dateTo) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="p-4 bg-slate-50 border-b border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Event Type</label>
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">All Events</option>
                  {Object.keys(EVENT_CONFIG).map((type) => (
                    <option key={type} value={type}>
                      {EVENT_CONFIG[type].label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Meeting ID</label>
                <Input
                  placeholder="Filter by meeting..."
                  value={filters.meetingId}
                  onChange={(e) => setFilters({ ...filters, meetingId: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">From Date</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">To Date</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
            </div>
          )}

          {/* Event List */}
          <div className="divide-y divide-slate-100">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-slate-500">Loading audit events...</p>
              </div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No audit events found</p>
                <p className="text-sm text-slate-400 mt-1">Events will appear here as actions occur</p>
              </div>
            ) : (
              events.map((event) => {
                const config = getEventConfig(event.event_type);
                const Icon = config.icon;
                const isExpanded = selectedEvent === event.id;

                return (
                  <div key={event.id} className="hover:bg-slate-50 transition-colors">
                    <button
                      onClick={() => setSelectedEvent(isExpanded ? null : event.id)}
                      className="w-full p-4 flex items-center gap-4 text-left"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${config.color}-50`}>
                        <Icon className={`w-5 h-5 text-${config.color}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{config.label}</span>
                          {event.meeting_id && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                              Meeting: {event.meeting_id.slice(0, 12)}...
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(event.created_at)}
                          </span>
                          {event.user_id && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {event.user_id.slice(0, 12)}...
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight
                        className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                      />
                    </button>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 pl-18">
                        <div className="ml-14 p-4 bg-slate-50 rounded-lg">
                          <h4 className="text-sm font-medium text-slate-700 mb-3">Event Details</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Event ID:</span>
                              <span className="ml-2 font-mono text-slate-700">{event.id}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Timestamp:</span>
                              <span className="ml-2 text-slate-700">{formatTimestamp(event.created_at)}</span>
                            </div>
                            {event.meeting_id && (
                              <div>
                                <span className="text-slate-500">Meeting ID:</span>
                                <span className="ml-2 font-mono text-slate-700">{event.meeting_id}</span>
                              </div>
                            )}
                            {event.user_id && (
                              <div>
                                <span className="text-slate-500">User ID:</span>
                                <span className="ml-2 font-mono text-slate-700">{event.user_id}</span>
                              </div>
                            )}
                          </div>
                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                            <div className="mt-4">
                              <span className="text-sm text-slate-500">Metadata:</span>
                              <pre className="mt-2 p-3 bg-white rounded border border-slate-200 text-xs font-mono overflow-x-auto">
                                {JSON.stringify(event.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                          {event.meeting_id && (
                            <div className="mt-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/meeting/${event.meeting_id}`)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Meeting
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
